/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { DefaultCaps, Director, MoveActor, Stage, StageHelper, Text2D, Text2DAlignment, Text2DBox, Text2DFont, Text2DFontLoader, CapNames } from '@sick-davinci/glow';
import { Color, Mesh, MeshBasicMaterial, PlaneBufferGeometry, TextureLoader, Vector3 } from 'three';
import * as dat from 'three/examples/js/libs/dat.gui.min.js';

/**
 * Demo:    Text2DDemo
 * Description:
 *  This demo application shows how to display a 2D text in various ways
 * Features:
 *  - Orbit navigation with custom settings
 *  - Capability handling
 *  - ViewAPI
 */
const text = `It is a period of civil war.
Rebel spaceships, striking
from a hidden base, have won
their first victory against
the evil Galactic Empire.

During the battle, Rebel
spies managed to steal secret
plans to the Empire's
ultimate weapon, the DEATH
STAR, an armored space
station with enough power to
destroy an entire planet.

Pursued by the Empire's
sinister agents, Princess
Leia races home aboard her
starship, custodian of the
stolen plans that can save
her people and restore
freedom to the galaxy.....`;

let _font1: Text2DFont;
let _font2: Text2DFont;
let _font3: Text2DFont;

const fontController = {
  fontType: 'Sans Serif',
  alignment: 'CENTER',
  billboard: false,
  flipY: false,
  lineHeight: 2.1,
  lineSpacing: 0,
  color: '#ffff00',
  boxWidth: 10,
  opacity: 1,
};

let textRenderer: Text2D;

const textChanger = () => {

  const font = fontController.fontType === 'Sans Serif' ? _font3 : (fontController.fontType === 'Serif' ? _font1 : _font2);

  textRenderer.setFont(font);
  textRenderer.setLineHeight(fontController.lineHeight);
  textRenderer.setLineSpacing(fontController.lineSpacing);
  textRenderer.setAlignment((Text2DAlignment as any)[fontController.alignment]);
  textRenderer.setText(text, fontController.alignment === 'BLOCK');
  textRenderer.enableFlipY(fontController.flipY);
  font.setStyle({
    color: new Color(fontController.color),
    asBillboard: fontController.billboard,
    opacity: fontController.opacity,
  });

  const box = { left: -fontController.boxWidth, top: 0, right: fontController.boxWidth, bottom: -1 } as Text2DBox;
  textRenderer.setTextBox(box);
};

function createControl() {
  const gui = new dat.GUI();
  gui.close();
  gui.add(fontController, 'fontType').options(['Sans Serif', 'Serif', 'MonoSpaced']).onChange(textChanger);
  gui.add(fontController, 'alignment').options(['LEFT', 'RIGHT', 'CENTER', 'BLOCK']).onChange(textChanger);
  gui.add(fontController, 'billboard', false).onChange(textChanger);
  gui.add(fontController, 'flipY', false).onChange(textChanger);
  gui.add(fontController, 'lineHeight', 0.1, 5.0, .1).onChange(textChanger);
  gui.add(fontController, 'lineSpacing', 0.0, 1.0, .1).onChange(textChanger);
  gui.add(fontController, 'boxWidth', 4, 50.0, .5).onChange(textChanger);
  gui.addColor(fontController, 'color').onChange(textChanger);
  gui.add(fontController, 'opacity', 0.0, 1.0, .1).onChange(textChanger);
}

export const run = ((viewParent: HTMLElement) => {
  createControl();

  // * Setup the stage *
  const desc = StageHelper.getDefaultSetup();
  desc.cameras[0].fov = 60;
  desc.cameras[0].from = [0, -30, 15];
  desc.cameras[0].to = [0, 0, 0];
  desc.viewports[0].bgcolor = 0x000000;

  const stage = new Stage(viewParent, desc);

  // * Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director);

  stage.applyThreeDirect((scn) => {

    const loader = new TextureLoader();
    const tex = loader.load('./resources/logo.png');

    const material = new MeshBasicMaterial({ map: tex });
    const plane = new PlaneBufferGeometry(10, 6);
    plane.rotateX(Math.PI / 2.0);
    plane.translate(0, 30, 10);
    const logo = new Mesh(plane, material);
    scn.add(logo);
  });

  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0x999999 } });

  const lh = 2.1;
  Text2DFontLoader.getFont('./resources/NotoSerifJP-Regular.json', './resources/').then((font1) => {
    Text2DFontLoader.getFont('./resources/CutiveMono-Regular.json', './resources/').then((font2) => {
      Text2DFontLoader.getFont('./resources/OpenSans-Regular.json', './resources/').then((font3) => {

        _font1 = font1;
        _font2 = font2;
        _font3 = font3;

        const style = { color: new Color(1, 1, 0), asBillboard: false };
        _font1.setStyle(style);
        _font2.setStyle(style);
        _font3.setStyle(style);

        textRenderer = new Text2D(font3);

        const box = { left: -15, top: 0, right: 15, bottom: -lh } as Text2DBox;
        textRenderer.setTextBox(box);

        textRenderer.setLineHeight(lh);
        textRenderer.enableAutoWrapping(false);
        textRenderer.setAlignment(Text2DAlignment.CENTER);

        stage.applyThreeDirect((scene) => {
          scene.add(textRenderer);
          textRenderer.setText(text);

          const ret = new MoveActor(scene.getStage(), textRenderer, 'textmove');
          ret.setDuration(60);
          ret.addTarget('position', 'vector3', new Vector3(0, -22, 0), new Vector3(0, 60, 0));
          stage.addActor(ret);
        },
        );
      });
    });
  });

  stage.start();
});
